import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Award, Download, ExternalLink, Anchor, Users, TrendingUp, ShieldCheck, MapPin } from "lucide-react";

/**
 * /partners — a private-feeling landing page designed for grant reviewers,
 * SBA lenders, and prospective equity investors. Not linked from the main
 * public nav; share the URL directly with reviewers.
 */
export default function Partners() {
  return (
    <div data-testid="partners-page" className="container-editorial py-16 max-w-5xl">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/30 px-4 py-2 text-xs uppercase tracking-[0.2em] text-accent mb-6">
          <Award className="h-3.5 w-3.5" strokeWidth={1.75}/>
          <span>Veteran-Owned · Service-Disabled · Iraq War</span>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl mb-4 leading-tight">
          For Lenders,<br/>
          <span className="italic text-accent">Grantors & Investors.</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          The private one-pager on SlipShare — the built, live, Airbnb-for-boat-docks marketplace, founded by a
          combat-tour U.S. Army veteran with a 60% service-connected disability rating.
        </p>
      </div>

      {/* Founder card */}
      <section className="rounded-3xl border border-border bg-card p-8 sm:p-12 mb-12">
        <div className="grid sm:grid-cols-3 gap-8">
          <div className="sm:col-span-1">
            <div className="w-24 h-24 rounded-full bg-accent/10 flex items-center justify-center mb-4">
              <span className="font-display text-4xl text-accent">BA</span>
            </div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Founder & CEO</div>
            <div className="font-display text-2xl">Blake Almond</div>
          </div>
          <div className="sm:col-span-2 space-y-4 text-foreground/85 leading-relaxed">
            <p>
              <strong className="text-foreground">U.S. Army veteran</strong> · one combat tour, Operation Iraqi Freedom.
              Honorable discharge. <strong className="text-foreground">60% service-connected VA disability rating.</strong>
            </p>
            <p>
              Sole founder, designer, and engineer of SlipShare. Built the full-stack marketplace personally over
              several months using AI-assisted development tools. Deployed and operational at boatport.org today.
            </p>
            <p>
              Pursuing formal <strong className="text-foreground">Service-Disabled Veteran-Owned Small Business (SDVOSB)</strong> certification
              through the SBA.
            </p>
          </div>
        </div>
      </section>

      {/* Traction */}
      <section className="grid sm:grid-cols-3 gap-4 mb-12">
        {[
          { icon: Anchor, label: "Product Status", value: "Live", sub: "Deployed at boatport.org" },
          { icon: TrendingUp, label: "Year-1 Target GBV", value: "$500K", sub: "10% platform fee = $50K rev" },
          { icon: Users, label: "Y3 Projected Revenue", value: "$4M", sub: "Path to profitability" },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-6">
            <s.icon className="h-5 w-5 text-accent mb-4" strokeWidth={1.5}/>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{s.label}</div>
            <div className="font-display text-3xl mb-1">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.sub}</div>
          </div>
        ))}
      </section>

      {/* The Market */}
      <section className="mb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-accent mb-3">The Market</div>
        <h2 className="font-display text-3xl mb-5">A 10-to-1 supply gap, structurally.</h2>
        <div className="grid sm:grid-cols-2 gap-8 text-foreground/85">
          <div>
            <div className="font-display text-4xl mb-1">12M+</div>
            <p className="text-sm text-muted-foreground">registered recreational boats in the U.S.</p>
          </div>
          <div>
            <div className="font-display text-4xl mb-1">1.1M</div>
            <p className="text-sm text-muted-foreground">marina and private slips available.</p>
          </div>
        </div>
        <p className="mt-6 text-foreground/85">
          Marina waitlists run <strong>3 to 10 years long</strong> in every desirable boating market.
          Meanwhile, millions of waterfront homeowners have private docks that sit empty <strong>300+ days a year.</strong>
          SlipShare unlocks that latent supply — a $3.2B annual U.S. slip/dockage market with no dominant P2P player.
        </p>
      </section>

      {/* Product moat */}
      <section className="mb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-accent mb-3">Built-In Moats</div>
        <h2 className="font-display text-3xl mb-6">Not just an idea. A functional platform.</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            {
              icon: ShieldCheck,
              title: "Regulatory Compliance Guardrail",
              body: "Automatically blocks listings on 35+ federally-restricted waterways (USACE / TVA / Entergy). Competitors would need years to build the same regulatory intelligence.",
            },
            {
              icon: Users,
              title: "Two-Sided Referral Flywheel",
              body: "Built-in $100 / $250 / $500 milestone rewards for referrers. Viral growth engineered into the product, not bolted on.",
            },
            {
              icon: MapPin,
              title: "Coastal + Inland Coverage",
              body: "Full support for lakes, bays, coasts, and intracoastal waterways. Not limited to any single geography.",
            },
            {
              icon: Award,
              title: "Veteran-Owned Trust Signal",
              body: "70% of U.S. consumers prefer to buy from veteran-owned businesses when given the choice (Hire Heroes USA, 2023). This is a marketing advantage most marketplaces can't buy.",
            },
          ].map((m, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-6">
              <m.icon className="h-5 w-5 text-accent mb-4" strokeWidth={1.5}/>
              <h3 className="font-medium mb-2">{m.title}</h3>
              <p className="text-sm text-foreground/75 leading-relaxed">{m.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The Ask */}
      <section className="rounded-3xl bg-primary text-primary-foreground p-10 mb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-accent-foreground/70 mb-3">The Ask</div>
        <h2 className="font-display text-4xl mb-6">$150,000 to prove the model in 12 months.</h2>
        <div className="grid sm:grid-cols-2 gap-8 text-sm">
          <div>
            <div className="font-medium mb-3">Blended raise, prioritized by veteran-first capital:</div>
            <ol className="list-decimal ml-5 space-y-2 text-primary-foreground/85">
              <li>VA Chapter 31 VR&E — Self-Employment Track ($40–60K target)</li>
              <li>Veteran-focused grants — Warrior Rising, Hello Alice, Comcast RISE ($15–40K)</li>
              <li>SBA Veterans Advantage Loan ($50–100K)</li>
              <li>Equity investment if needed to close gap ($0–50K)</li>
            </ol>
          </div>
          <div>
            <div className="font-medium mb-3">In exchange:</div>
            <ul className="list-disc ml-5 space-y-2 text-primary-foreground/85">
              <li>To grantors: direct support of a service-disabled veteran translating military discipline into economic impact</li>
              <li>To SBA lenders: personal guarantees + a live, revenue-ready product with regulatory compliance built in</li>
              <li>To equity investors: a negotiated stake in a functioning platform in a $3.2B market</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Download the plan */}
      <section className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.2em] text-accent mb-3">Documentation</div>
        <h2 className="font-display text-3xl mb-6">Read the full plan.</h2>
        <div className="flex flex-wrap gap-3 justify-center">
          <a
            href="/SlipShare_Business_Plan.pdf"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-business-plan-btn"
          >
            <Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
              <Download className="h-4 w-4 mr-2" strokeWidth={1.75}/>
              Download Business Plan (PDF)
            </Button>
          </a>
          <Link to="/">
            <Button size="lg" variant="outline" className="rounded-full" data-testid="view-live-product-btn">
              <ExternalLink className="h-4 w-4 mr-2" strokeWidth={1.75}/>
              Try the Live Product
            </Button>
          </Link>
        </div>
      </section>

      {/* Contact */}
      <section className="border-t border-border pt-10 text-center">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Direct Contact</div>
        <div className="font-display text-2xl mb-1">Blake Almond</div>
        <div className="text-sm text-muted-foreground mb-1">Founder &amp; CEO · U.S. Army Veteran (Iraq)</div>
        <div className="text-sm">
          <a href="mailto:blakealmond@outlook.com" className="text-accent hover:underline">blakealmond@outlook.com</a>
          {" · "}
          <a href="https://boatport.org" className="text-accent hover:underline">boatport.org</a>
        </div>
      </section>
    </div>
  );
}
